import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  GatewayHealthEntry,
  GatewaysHealthActions,
  GatewaysHealthData,
} from '@/console/features/gateways/hooks/use-gateway-health/gateways-health-context.types';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadGatewaysHealthStatusQuery } from '@/shared/redux/api/assets/gateways/gateways-api';

export const GATEWAYS_HEALTH_POLLING_INTERVAL_MS = 60_000;

const GatewaysHealthActionsContext =
  createContext<GatewaysHealthActions | null>(null);

const GatewaysHealthDataContext = createContext<GatewaysHealthData | null>(
  null,
);

export const useGatewaysHealthActions = (): GatewaysHealthActions | null =>
  useContext(GatewaysHealthActionsContext);

export const useGatewaysHealthData = (): GatewaysHealthData | null =>
  useContext(GatewaysHealthDataContext);

export const GatewaysHealthProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const refCounts = useRef<Map<string, number>>(new Map());
  const [ids, setIds] = useState<string[]>([]);

  const recomputeIds = useCallback(() => {
    setIds(Array.from(refCounts.current.keys()).sort());
  }, []);

  const register = useCallback(
    (gatewayId: string) => {
      const next = (refCounts.current.get(gatewayId) ?? 0) + 1;
      refCounts.current.set(gatewayId, next);
      if (next === 1) {
        recomputeIds();
      }
    },
    [recomputeIds],
  );

  const unregister = useCallback(
    (gatewayId: string) => {
      const current = refCounts.current.get(gatewayId) ?? 0;
      if (current <= 1) {
        refCounts.current.delete(gatewayId);
        recomputeIds();
      } else {
        refCounts.current.set(gatewayId, current - 1);
      }
    },
    [recomputeIds],
  );

  const actions = useMemo<GatewaysHealthActions>(
    () => ({ register, unregister }),
    [register, unregister],
  );

  const { data, isLoading } = usePollingQuery({
    useQueryHook: useReadGatewaysHealthStatusQuery,
    queryArg: { gatewayIds: ids },
    pollingInterval: GATEWAYS_HEALTH_POLLING_INTERVAL_MS,
    queryOptions: { skip: ids.length === 0 },
  });

  const dataValue = useMemo<GatewaysHealthData>(() => {
    const states: Record<string, GatewayHealthEntry> = {};
    data?.readGatewaysByIds?.items?.forEach((item) => {
      if (item.gatewayId) {
        states[item.gatewayId] = {
          state: item.state?.state,
          reportedAt: item.state?.reportedAt,
        };
      }
    });
    return { states, isLoading };
  }, [data, isLoading]);

  return (
    <GatewaysHealthActionsContext.Provider value={actions}>
      <GatewaysHealthDataContext.Provider value={dataValue}>
        {children}
      </GatewaysHealthDataContext.Provider>
    </GatewaysHealthActionsContext.Provider>
  );
};
